<template>
  <div id="audit-logs">
      <div>
        <div class="title-card">
          <div class="date-picker-container bg-[#13191D] rounded-md h-[40px] flex items-center w-54">
            <date-range-picker
              ref="picker"
              :opens="'right'"
              :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy HH:mm:ss' }"
              :singleDatePicker="false"
              :ranges="false"
              :timePicker="false"
              :timePicker24Hour="false"
              :showWeekNumbers="false"
              :showDropdowns="false"
              :autoApply="true"
              v-model="filter.createdAt"
              :linkedCalendars="true"
              class="w-full"
              >
              <template v-slot:input="picker" class="relative font-bold">
                  <div class="text-base sm:text-sm text-[#CDD6EE] px-2 h-full flex items-center gap-3">
                    <i class="el-icon-date relative -top-0.5"></i>
                    <span v-if="picker.startDate && picker.endDate">
                      {{ moment(picker.startDate).format('DD/MM/YYYY')}} - {{ moment(picker.endDate).format('DD/MM/YYYY') }}</span>
                    <span v-else class="text-gray-400">Select Date Range</span>
                  </div>
              </template>
            </date-range-picker>
          </div>
          <div>
            <el-select v-model="filter.targetType" placeholder="Select target type" clearable>
              <el-option
                v-for="item in targetTypes"
                :key="item"
                :label="item"
                :value="item">
              </el-option>
            </el-select>
          </div>
          <div>
            <el-select v-model="filter.action" placeholder="Select action" clearable>
              <el-option
                v-for="item in actions"
                :key="item"
                :label="item"
                :value="item">
              </el-option>
            </el-select>
          </div>
          <div>
            <el-select v-model="filter.status" placeholder="Status" clearable>
              <el-option
                v-for="item in statuses"
                :key="item"
                :label="item"
                :value="item">
              </el-option>
            </el-select>
          </div>
          <div>
            <el-input v-model="filter.userEmail" placeholder="User Email" clearable></el-input>
          </div>
        </div>
        <div class="content-wrapper"
          v-loading="loading"
          element-loading-text="Loading..."
          element-loading-background="#13191d">
          <div class="item-list infinite-list">
            <div
              v-for="(group, key) in groupCurrentLogs"
              :key="key">
              <el-divider content-position="left">
                <strong>{{key | date('DD/MM/YYYY')}}</strong>
              </el-divider>
              <div class="panel item-list--item"
                v-for="item in group" :key="item.id"
                >
                <div class="item-list--item-date">
                  <div>{{item.createdAt | date('HH:mm')}}</div>
                </div>
                <div>
                  <span class="name">{{item.details?.log || item.message || item.action}}</span>
                  <div class="log-tags">
                    <el-tag size="small" :style="{background: typeColor(item.targetType)}" v-if="item.targetType">
                      {{item.targetType}}
                    </el-tag>
                    <el-tag size="small" :style="{background: typeColor(item.action)}" v-if="item.action">
                      {{item.action}}
                    </el-tag>
                    <el-tag size="small" :style="{background: statusColor(item.status)}" v-if="item.status">
                      {{item.status}}
                    </el-tag>
                  </div>
                </div>
                <div class="item-list--item-right">
                  <div>
                    <span v-if="item.targetName">{{item.targetName}}</span>
                    <span v-else-if="item.targetId" class="warning" style="font-size: 0.8em;">{{item.targetId}}</span>
                  </div>
                  <div class="item-list--item-right-user">
                    <span v-if="item.userEmail">{{item.userEmail}}</span>
                    <span v-else class="warning">System</span>
                  </div>
                </div>
              </div>
            </div>

            <p v-if="loading">Loading...</p>
            <p v-if="noMore && logs && logs.items && logs.items.length > 0">No more entries</p>
            <p v-if="(!logs || !logs.items || logs.items.length === 0) && !loading">No logs found</p>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import ColorGenerator from '@/services/ColorGenerator';
import _ from 'lodash';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  components: {
    DateRangePicker,
  },
  data() {
    return {
      moment, // expose moment to template
      filter: {
        createdAt: { startDate: null, endDate: null },
        action: '',
        targetType: '',
        status: '',
        userEmail: '',
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
      },
    };
  },

  computed: {
    ...mapState({
      logs: (state) => state.auditLogs.logs,
      loading: (state) => state.auditLogs.loading,
    }),

    sortedLogs() {
      if (!this.logs || !this.logs.items) return [];
      return [...this.logs.items]
        .sort((left, right) => moment.utc(right.createdAt).diff(moment.utc(left.createdAt)));
    },

    groupCurrentLogs() {
      return _.groupBy(this.sortedLogs, (date) => moment(date.createdAt).startOf('day'));
    },

    noMore() {
      return !this.logs || !this.logs.nextToken;
    },

    targetTypes() {
      if (!this.logs || !this.logs.items) return [];
      const types = this.logs.items.map(i => i.targetType).filter(i => !!i);
      return _.uniq(types);
    },

    actions() {
      if (!this.logs || !this.logs.items) return [];
      const acts = this.logs.items.map(i => i.action).filter(i => !!i);
      return _.uniq(acts);
    },

    statuses() {
      return ['SUCCESS', 'FAILURE', 'WARNING', 'INFO'];
    },

    typeColor() {
      return (type) => ColorGenerator.stringToColor(type || 'default');
    },

    statusColor() {
      return (status) => {
        if (status === 'SUCCESS') return '#67C23A';
        if (status === 'FAILURE') return '#F56C6C';
        if (status === 'WARNING') return '#E6A23C';
        if (status === 'INFO') return '#909399';
        return ColorGenerator.stringToColor(status || 'default');
      };
    },

    logFilter() {
      const cleanFilter = {};
      const modelStringInput = {
        attributeExists: true,
      };

      if (this.filter.createdAt && this.filter.createdAt.startDate && this.filter.createdAt.endDate) {
        const startOfDay = moment(this.filter.createdAt.startDate).startOf('day').toISOString();
        const endOfDay = moment(this.filter.createdAt.endDate).endOf('day').toISOString();
        cleanFilter.createdAt = {
          between: [startOfDay, endOfDay]
        };
      }

      if (this.filter.action && this.filter.action !== '') {
        cleanFilter.action = { eq: this.filter.action };
      }

      if (this.filter.targetType && this.filter.targetType !== '') {
        cleanFilter.targetType = { eq: this.filter.targetType };
      }

      if (this.filter.status && this.filter.status !== '') {
        cleanFilter.status = { eq: this.filter.status };
      }

      if (this.filter.userEmail && this.filter.userEmail !== '') {
        cleanFilter.userEmail = { contains: this.filter.userEmail };
      }

      if (_.isEmpty(cleanFilter)) return null;

      return cleanFilter;
    },
  },

  watch: {
    logFilter: {
      deep: true,
      async handler(newValue) {
        if (newValue) {
          await this.filterAuditLogs(newValue);
        } else {
          await this.listAuditLogs({ limit: 100 });
        }
      }
    },
  },

  created() {
    this.listAuditLogs({ limit: 100 });
  },

  methods: {
    ...mapActions({
      listAuditLogs: 'auditLogs/listAuditLogs',
      filterAuditLogs: 'auditLogs/filterAuditLogs',
    }),
  },
};
</script>

<style>
.el-loading-spinner svg {
  margin: 0 auto;
}

#audit-logs .title-card {
  background: #343c49;
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr auto auto auto 1fr;
  grid-gap: 16px;
  padding: 16px;
  margin-bottom: 20px;
}
#audit-logs .title-card .el-input .el-input__inner {
  color: #fff;
  background: #13191D;
  border-color: #13191D;
}
#audit-logs .title-card .el-input input {
  background: transparent;
  color: #CDD6EE;
}
#audit-logs .vue-daterange-picker {
  background: transparent !important;
  width: 100%;
}
#audit-logs .vue-daterange-picker .form-control {
  border: none;
  background: transparent;
  color: #CDD6EE;
}

#audit-logs {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  margin: 20px;
  padding-top: 0;
}
#audit-logs .content-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
}
#audit-logs .el-divider {
  background-color: transparent;
}
#audit-logs .el-divider__text {
  background-color: #000;
  padding: 0 20px;
  color: #66718D;
  font-weight: 400;
  font-size: 16px;
  line-height: 30px;
}
#audit-logs .item-list--item {
  display: grid;
  grid-template-columns: 50px 1fr 1fr;
  margin-bottom: 6px;
  grid-gap: 16px;
  align-items: center;
  border-radius: 4px;
}
#audit-logs .item-list--item:hover {
  outline: 2px solid #ff9f31;
  color: #fff !important;
}
#audit-logs .item-list--item .name {
  color: #fff;
  margin-right: 16px;
}
#audit-logs .item-list--item .log-tags {
  display: inline-block;
}
#audit-logs .item-list--item-right {
  text-align: right;
  display: grid;
  grid-template-columns: 1fr 200px;
}
#audit-logs .item-list--item-right div {
  display: inline-block;
  margin-left: 16px;
}
#audit-logs .item-list--item-right-account {
  text-align: right;
  color: #CDD6EE;
}
#audit-logs .item-list--item .el-tag {
  color: #fff;
  border: 1px solid transparent;
}
#audit-logs .panel {
  position: relative;
  background: #232b33;
  padding: 8px 16px;
}
#audit-logs .panel .el-tag {
  margin-right: 16px;
}

@media screen and (max-width: 1000px), (orientation: portrait) {
  #audit-logs .title-card {
    grid-template-columns: auto;
    padding-right: 16px;
  }
  #audit-logs .title-card .el-select {
    width: 100%;
  }
  #audit-logs .item-list--item {
    display: grid;
    grid-template-columns: auto;
  }
  #audit-logs .item-list--item-date {
    position: absolute;
    top: 14px;
    left: 14px;
    font-size: 12px;
  }
  #audit-logs .item-list--item .name {
    margin-top: 10px;
    padding-top: 24px;
    display: inline-block;
  }
  #audit-logs .item-list--item .log-tags {
    position: absolute;
    top: 10px;
    right: 8px;
  }
  #audit-logs .item-list--item .log-tags .el-tag {
    margin-right: 0px;
    margin-left: 8px;
    font-size: 11px;
    border-radius: 3px;
  }
  #audit-logs .item-list--item-right {
    text-align: left;
    grid-template-columns: 1fr;
  }
  #audit-logs .item-list--item-right div {
    margin-left: 0px;
  }
}
</style>
